import type { AccessMember } from '@/api/core/access-members';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import {
  cancelGuestInvitation,
  createGuestInvitation,
  readGuestInvitations,
  updateGuestInvitation,
} from '@/api/core/guest-invitations';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { isUuid } from '@/lib/validation';
import { Button, Input, ListPage, PageHeader } from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { propertyRuntime, type PropertyRuntime } from '../property/property-queries';

/** datetime-local은 지점 업무 시간인 KST로 해석한다. */
function utcFromKst(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const date = new Date(`${value}:00+09:00`);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}
const kstFormatter = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** 관리자는 현재 게스트 초대의 마스킹 목록과 기간을 관리한다. */
export function GuestInvitationsPage({ runtime = propertyRuntime }: { runtime?: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  return <GuestInvitationsContent key={session.generation} runtime={runtime} />;
}

function GuestInvitationsContent({ runtime }: { runtime: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  const qc = useQueryClient();
  const allowed =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 1);
  const writable =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 4);
  const currentWrite = () => {
    const current = runtime.session.getSnapshot();
    return (
      current.generation === session.generation &&
      current.status === 'authorized' &&
      !!current.authorization &&
      !current.deniedDomains.includes('entitlement') &&
      canReadDomain(current.authorization, 'entitlement', 4)
    );
  };
  const [cursor, setCursor] = useState<string>();
  const key = [
    ...coreQueryKey(runtime.apiNamespace, session.generation, 'guest-invitations'),
    cursor ?? '',
  ];
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => readGuestInvitations(runtime.client, signal, cursor),
    enabled: allowed,
  });
  const [stay, setStay] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [editing, setEditing] = useState<AccessMember>();
  const [cancel, setCancel] = useState<AccessMember>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const save = async () => {
    const startAt = utcFromKst(start),
      endAt = utcFromKst(end);
    if (!writable || !currentWrite() || busy) return;
    if (
      !startAt ||
      !endAt ||
      startAt >= endAt ||
      (!editing && (!isUuid(stay) || !name.trim() || !phone.trim()))
    ) {
      setMessage('숙박과 방문 기간, 초대 정보를 확인해 주세요.');
      return;
    }
    const generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      const result = editing
        ? await updateGuestInvitation(runtime.client, editing.id, {
            command_id: crypto.randomUUID(),
            expected_revision: editing.revision,
            visit_start: startAt,
            visit_end: endAt,
          })
        : await createGuestInvitation(runtime.client, {
            command_id: crypto.randomUUID(),
            id: crypto.randomUUID(),
            stay_id: stay,
            name: name.trim(),
            phone,
            visit_start: startAt,
            visit_end: endAt,
          });
      if (runtime.session.getSnapshot().generation !== generation) return;
      setEditing(undefined);
      setPhone('');
      setMessage(
        result.message_sent === false
          ? '초대는 저장됐지만 알림 발송에 실패했습니다.'
          : '게스트 초대를 저장했습니다.',
      );
      await qc.invalidateQueries({ queryKey: key });
    } catch (error) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      setMessage(
        isApiError(error) && error.status === 409
          ? '현재 초대가 변경되었습니다. 다시 조회해 주세요.'
          : '게스트 초대를 저장하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    if (!cancel || !writable || !currentWrite() || busy) return;
    const generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      await cancelGuestInvitation(runtime.client, cancel.id, {
        command_id: crypto.randomUUID(),
        expected_revision: cancel.revision,
      });
      if (runtime.session.getSnapshot().generation !== generation) return;
      setCancel(undefined);
      setMessage('게스트 초대를 취소했습니다.');
      await qc.invalidateQueries({ queryKey: key });
    } catch (error) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      setMessage(
        isApiError(error) && error.status === 409
          ? '현재 초대가 변경되었습니다. 다시 조회해 주세요.'
          : '게스트 초대를 취소하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  if (session.status !== 'authorized') return null;
  return (
    <ListPage className="min-h-0 flex-1 overflow-y-auto">
      <ShellHeader>
        <PageHeader title="게스트 초대" />
      </ShellHeader>
      <div className="space-y-4 p-4">
        {allowed ? null : <p role="alert">게스트 조회 권한이 없습니다.</p>}
        {writable ? (
          <section aria-label="게스트 초대" className="space-y-2">
            {editing ? (
              <p>방문 기간 변경: {editing.name ?? editing.id}</p>
            ) : (
              <>
                <label>
                  Stay UUID
                  <Input
                    value={stay}
                    onChange={(e) => {
                      setStay(e.target.value);
                    }}
                  />
                </label>
                <label>
                  게스트 이름
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                  />
                </label>
                <label>
                  전화번호
                  <Input
                    type="tel"
                    autoComplete="off"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                    }}
                  />
                </label>
              </>
            )}
            <label>
              방문 시작 (한국 시간)
              <Input
                type="datetime-local"
                value={start}
                onChange={(e) => {
                  setStart(e.target.value);
                }}
              />
            </label>
            <label>
              방문 종료 (한국 시간)
              <Input
                type="datetime-local"
                value={end}
                onChange={(e) => {
                  setEnd(e.target.value);
                }}
              />
            </label>
            <Button disabled={busy} onClick={() => void save()}>
              {editing ? '기간 저장' : '게스트 초대'}
            </Button>
            {editing ? (
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(undefined);
                }}
              >
                변경 취소
              </Button>
            ) : null}
          </section>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
        {list.isError ? <p role="alert">게스트 초대를 불러오지 못했습니다.</p> : null}
        {allowed && list.data ? (
          <>
            <ul>
              {list.data.items.map((row) => (
                <li key={row.id} className="space-x-2">
                  <span>{row.name ?? '게스트'}</span>
                  <span>{row.phone_masked ?? '—'}</span>
                  <span>
                    {kstFormatter.format(new Date(row.valid_from))} ~{' '}
                    {kstFormatter.format(new Date(row.valid_until))}
                  </span>
                  {writable && !row.revoked_at ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditing(row);
                          setStart('');
                          setEnd('');
                        }}
                      >
                        기간 변경
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setCancel(row);
                        }}
                      >
                        취소
                      </Button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
            {list.data.next_cursor ? (
              <Button
                onClick={() => {
                  setCursor(list.data.next_cursor ?? undefined);
                }}
              >
                다음 게스트
              </Button>
            ) : null}
            {cursor ? (
              <Button
                variant="outline"
                onClick={() => {
                  setCursor(undefined);
                }}
              >
                처음 게스트
              </Button>
            ) : null}
          </>
        ) : null}
        {cancel ? (
          <section role="alert">
            <p>선택한 게스트 초대를 취소할까요?</p>
            <Button disabled={busy} onClick={() => void revoke()}>
              초대 취소 확인
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setCancel(undefined);
              }}
            >
              돌아가기
            </Button>
          </section>
        ) : null}
      </div>
    </ListPage>
  );
}
